import { useState } from 'react';

import { Button, Modal, ModalActions, ModalContent, ModalWrapper } from '@braskai/ui-kit';
import { useForm } from 'react-hook-form';

import { Stack } from '@mui/material';

import { useCreateAccount, useCreateUser } from '@/entities/user/currentUser';

import { useInterfaceLanguage } from '@/shared/i18n';
import { getLinkedInAttribution } from '@/shared/lib/attribution';
import { getOpenAiAdsContext } from '@/shared/lib/openAiAdsContext';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';
import { validateEmail } from '@/shared/lib/validators';
import { useRewardfulStore } from '@/shared/thirdParty/rewardful';
import { ControlledTextField } from '@/shared/ui/form';
import { ModalHeaderDefault } from '@/shared/ui/modal';

import { useAddEmailModalStore } from '../model/useAddEmailModalStore';

type FormValues = {
  confirmEmail: string;
  email: string;
};

const AddEmailModalComponent = () => {
  const { t } = useInterfaceLanguage();
  const referral = useRewardfulStore((state) => state.referralId);
  const { mutateAsync: createUser } = useCreateUser({
    isSocial: true,
  });
  const { mutateAsync: createAccount } = useCreateAccount();
  const onClose = useAddEmailModalStore((state) => state.close);

  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    formState: { isValid },
    watch,
    handleSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      confirmEmail: '',
      email: '',
    },
  });

  const email = watch('email');

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true);

    try {
      await createUser({
        email: values.email,
        linkedin: getLinkedInAttribution(),
        openai_ads: getOpenAiAdsContext(),
        referral_id: referral || '',
      });
      await createAccount({
        email: values.email,
        referral: referral || '',
      });
      onClose();
    } catch (e) {
      showErrorAlert(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal size="xs" open>
      <form data-testid="add-email-modal-form" onClick={handleSubmit(onSubmit)}>
        <ModalHeaderDefault title={t('account.addEmail.confirmTitle')} subtitle={t('account.addEmail.subtitle')} />
        <ModalContent noPadding>
          <Stack gap={12}>
            <ControlledTextField
              label={t('account.addEmail.emailLabel')}
              control={control}
              fieldName="email"
              placeholder={t('account.addEmail.emailPlaceholder')}
              type="email"
              validator={(value: string) => (validateEmail(value) ? t('account.addEmail.incorrectEmail') : undefined)}
              variant="outlined"
            />
            <ControlledTextField
              label={t('account.addEmail.repeatEmailLabel')}
              validator={(value: string) => {
                if (value.toLowerCase() !== email.toLowerCase()) {
                  return t('account.addEmail.emailMismatch');
                }

                return validateEmail(value) ? t('account.addEmail.incorrectEmail') : undefined;
              }}
              control={control}
              fieldName="confirmEmail"
              placeholder={t('account.addEmail.emailPlaceholder')}
              type="email"
              variant="outlined"
            />
          </Stack>
        </ModalContent>
        <ModalActions>
          <Button disabled={!isValid} isLoading={isLoading} size="xl" type="submit" fullWidth>
            {t('account.addEmail.nextQuestion')}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
};

export const AddEmailModal = () => (
  <ModalWrapper hook={useAddEmailModalStore}>
    <AddEmailModalComponent />
  </ModalWrapper>
);
